import * as z from 'zod';

import { PdfDocumentTypes } from '@zinnia/form-engine-sdk';
import { Language } from '../../../../localization';

const headerTextSchema = z.object({
  insuranceApplication: z.string().optional(),
  referenceNumber: z.string().optional(),
  additionalText: z.string().optional(),
});

export type HeaderText = z.infer<typeof headerTextSchema>;

const footerTextSchema = z.object({
  summaryFor: z.string(),
  additionalText: z.string().optional(),
});

export type FooterText = z.infer<typeof footerTextSchema>;

const pdfHeaderDataSchema = z.object({
  logo: z.object({
    alt: z.string(),
    base64ImageRepresentation: z.string(),
    height: z.string().optional(),
    width: z.string().optional(),
    url: z.string().optional(),
  }),
  secondaryLogo: z
    .object({
      alt: z.string(),
      base64ImageRepresentation: z.string(),
      height: z.string().optional(),
      width: z.string().optional(),
      url: z.string().optional(),
    })
    .optional(),
  title: z.string(),
  productInfo: z.string(),
  referenceNumber: z.string(),
  carrierName: z.string(),
  customHeaderText: z
    .object({
      [Language.en]: z
        .object({
          [PdfDocumentTypes.advisorsReport]: headerTextSchema,
          [PdfDocumentTypes.application]: headerTextSchema,
          [PdfDocumentTypes.carrierReport]: headerTextSchema,
          [PdfDocumentTypes.oneSpanEvidenceSummary]: headerTextSchema,
          [PdfDocumentTypes.external]: headerTextSchema,
          [PdfDocumentTypes.medicalInterviewAnswers]: headerTextSchema,
          [PdfDocumentTypes.pad]: headerTextSchema,
        })
        .partial(),
      [Language.fr]: z
        .object({
          [PdfDocumentTypes.advisorsReport]: headerTextSchema,
          [PdfDocumentTypes.application]: headerTextSchema,
          [PdfDocumentTypes.carrierReport]: headerTextSchema,
          [PdfDocumentTypes.oneSpanEvidenceSummary]: headerTextSchema,
          [PdfDocumentTypes.external]: headerTextSchema,
          [PdfDocumentTypes.medicalInterviewAnswers]: headerTextSchema,
          [PdfDocumentTypes.pad]: headerTextSchema,
        })
        .partial(),
    })
    .partial()
    .optional(),
  additionalHeaderText: z.string().optional(),
});

export type PdfHeaderData = z.infer<typeof pdfHeaderDataSchema>;

const pdfFooterDataSchema = z.object({
  referenceNumber: z.string(),
  fullName: z.string(),
  carrierName: z.string(),
  customFooterText: z
    .object({
      [Language.en]: z
        .object({
          [PdfDocumentTypes.advisorsReport]: footerTextSchema,
          [PdfDocumentTypes.application]: footerTextSchema,
          [PdfDocumentTypes.carrierReport]: footerTextSchema,
          [PdfDocumentTypes.oneSpanEvidenceSummary]: footerTextSchema,
          [PdfDocumentTypes.external]: footerTextSchema,
          [PdfDocumentTypes.medicalInterviewAnswers]: footerTextSchema,
          [PdfDocumentTypes.pad]: footerTextSchema,
        })
        .partial(),
      [Language.fr]: z
        .object({
          [PdfDocumentTypes.advisorsReport]: footerTextSchema,
          [PdfDocumentTypes.application]: footerTextSchema,
          [PdfDocumentTypes.carrierReport]: footerTextSchema,
          [PdfDocumentTypes.oneSpanEvidenceSummary]: footerTextSchema,
          [PdfDocumentTypes.external]: footerTextSchema,
          [PdfDocumentTypes.medicalInterviewAnswers]: footerTextSchema,
          [PdfDocumentTypes.pad]: footerTextSchema,
        })
        .partial(),
    })
    .partial()
    .optional(),
  additionalFooterText: z.string().optional(),
});

export type PdfFooterData = z.infer<typeof pdfFooterDataSchema>;

export const generatePDFHeaderAndFooterSchema = z.object({
  // @ts-ignore
  headerData: pdfHeaderDataSchema.deepPartial(),
  // @ts-ignore
  footerData: pdfFooterDataSchema.deepPartial(),
});

export type PdfGenerationOverrides = z.infer<typeof generatePDFHeaderAndFooterSchema>;
